import { AddCircleOutline as AddCircleOutlineIcon } from '@mui/icons-material'
import { Alert, Box, Button, Collapse, Stack } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from '../../../../../common/hooks/helper/useTranslation'
import { TreatmentStatus } from '../../../../../models/graphqlTypes'
import { AddSurveyModalParams } from '../Survey/AddSurveyModal'
import {
  AddAppointmentModalParams,
  TreatmentPanelTab,
} from '../../types/treatmentPanel.types'
import { AddProtocolButton } from './AddProtocolButton'
import { TREATMENT_CALENDAR_WIDTH } from '../../../../../config'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ImportContactsIcon from '@mui/icons-material/ImportContacts'
import { SendTreatmentButton } from './TreatmentPanelButtons/SendTreatmentButton'
import { More } from '../../../../../common/components/BetmenList/BetmenListActions'
import { SaveTreatmentButton } from './TreatmentPanelButtons/SaveTreatmentButton'
import { DeleteTreatmentButton } from './TreatmentPanelButtons/DeleteTreatmentButton'
import { useBlackButtonStyle } from '../../../../../utils/styles/useBlackButtonStyle'
import { BetmenListFilter } from '../../../../../common/components'
import { useSelectedLanguage } from '../../../../../common/hooks/useSelectedLanguage'
import { BooleanFilter } from '../../../../../common/components/Filters/BooleanFilter'
import { useTreatmentPanelData } from './TreatmentPanelProvider'

type Props = {
  toggleAddAppointmentModal: React.Dispatch<
    React.SetStateAction<AddAppointmentModalParams>
  >
  toggleAddSurveyModal: React.Dispatch<
    React.SetStateAction<AddSurveyModalParams>
  >
}

const TreatmentPanelSubHeader: React.FC<React.PropsWithChildren<Props>> = ({
  toggleAddAppointmentModal,
  toggleAddSurveyModal,
}) => {
  const {
    treatment,
    isOwnTreatment,
    activeView,
    toggleActiveView,
    filterValue,
    setFilterValue,
    onOpenReferralModal,
  } = useTreatmentPanelData()

  const { t } = useTranslation()
  const selectedLanguage = useSelectedLanguage()

  const [showInfo, setShowInfo] = useState<boolean>(false)

  const classes = useBlackButtonStyle()

  const isDeleted = treatment?.status === TreatmentStatus.Cancelled

  return (
    <Box display="flex" py={1} px={2}>
      <Stack ml={`${TREATMENT_CALENDAR_WIDTH}px`} flex={1} gap={1}>
        <Stack direction="row" gap={1} alignItems="center">
          <BetmenListFilter
            searchTerm={filterValue.searchTerm}
            handleSearch={(searchTerm) =>
              setFilterValue((prevValue) => ({
                ...prevValue,
                searchTerm: searchTerm ?? '',
              }))
            }
          />

          <BooleanFilter
            filterInput={!!filterValue.isAppointment}
            setFilterInput={(newFilter: boolean) =>
              setFilterValue((prevInput) => ({
                ...prevInput,
                isAppointment: newFilter,
              }))
            }
            label={t('common:appointment')}
          />
          <BooleanFilter
            filterInput={!!filterValue.isSurvey}
            setFilterInput={(newFilter: boolean) =>
              setFilterValue((prevInput) => ({
                ...prevInput,
                isSurvey: newFilter,
              }))
            }
            label={t('common:survey')}
          />
          <BooleanFilter
            filterInput={!!filterValue.isReferral}
            setFilterInput={(newFilter: boolean) =>
              setFilterValue((prevInput) => ({
                ...prevInput,
                isReferral: newFilter,
              }))
            }
            label={t('common:referral')}
          />

          <Button
            size="small"
            color="primary"
            variant="text"
            startIcon={<InfoOutlinedIcon />}
            sx={{ paddingX: 1, minWidth: 'fit-content', ml: 'auto' }}
            classes={{ textPrimary: classes.textPrimary }}
            onClick={() => setShowInfo(true)}
          >
            {t('treatment:description')}
          </Button>
          <Button
            size="small"
            color="primary"
            variant="text"
            startIcon={<ImportContactsIcon />}
            sx={{ paddingX: 1, minWidth: 'fit-content' }}
            onClick={toggleActiveView}
            classes={{ textPrimary: classes.textPrimary }}
          >
            {activeView === TreatmentPanelTab.Events
              ? t('treatment:log')
              : t('treatment:treatmentpanel.showAppointments')}
          </Button>

          <SendTreatmentButton />

          <More>
            <SaveTreatmentButton />
            <DeleteTreatmentButton />
          </More>
        </Stack>

        <Collapse in={showInfo} unmountOnExit>
          <Alert onClose={() => setShowInfo(false)} severity="info">
            {treatment.description[selectedLanguage]}
          </Alert>
        </Collapse>

        {!isDeleted && isOwnTreatment && (
          <Stack direction="row" gap={1} alignItems="center">
            <AddProtocolButton />
            <Button
              size="small"
              color="primary"
              variant="text"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => toggleAddAppointmentModal({ isOpen: true })}
              data-cy="AppointmentEmptyState-Button-addNewAppointment"
              sx={{ paddingX: 2 }}
            >
              {t('treatment:addNewAppointment')}
            </Button>
            <Button
              size="small"
              color="primary"
              variant="text"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => toggleAddSurveyModal({ isOpen: true })}
              data-cy="AppointmentEmptyState-Button-addNewSurvey"
              sx={{ paddingX: 2 }}
            >
              {t('treatment:addNewSurvey')}
            </Button>
            <Button
              size="small"
              color="primary"
              variant="text"
              startIcon={<AddCircleOutlineIcon />}
              onClick={onOpenReferralModal}
              sx={{ paddingX: 2 }}
            >
              {t('treatment:addNewReferral')}
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  )
}

export { TreatmentPanelSubHeader }
